import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Link,
  Checkbox,
  FormGroup,
  FormControlLabel,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Stack,
  LinearProgress,
  Chip,
  IconButton
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  Email as EmailIcon,
  Lock as PasswordIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  Gavel as GavelIcon,
  Security as SecurityIcon,
  Google as GoogleIcon,
  Apple as AppleIcon,
  Facebook as FacebookIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as UncheckedIcon,
  Dns as ServerIcon,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import NebenanHilfeLogo from '../UI/NebenanHilfeLogo';
import { signInWithGooglePopup } from '../../services/googleAuth';

const RegisterForm = ({ onSwitchToLogin, onRequireVerification, onRequireMFA }) => {
  const { register, socialLogin } = useAuth();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    postalCode: '',
    password: '',
    passwordConfirm: '',
    acceptTerms: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [legalModal, setLegalModal] = useState({ open: false, title: '', type: '' });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
    setError('');
  };

  const handleOpenAgb = (e) => {
    e.preventDefault();
    setLegalModal({
      open: true,
      title: 'Allgemeine Geschäftsbedingungen (AGB)',
      type: 'agb',
    });
  };

  const handleOpenPrivacy = (e) => {
    e.preventDefault();
    setLegalModal({
      open: true,
      title: 'Datenschutzerklärung (DSGVO)',
      type: 'privacy',
    });
  };

  const handleCloseModal = () => {
    setLegalModal({ open: false, title: '', type: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.firstName || !formData.lastName || !formData.email || !formData.postalCode || !formData.password) {
      setError('Bitte fülle alle Pflichtfelder aus.');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Bitte eine gültige E-Mail-Adresse eingeben.');
      return;
    }

    if (formData.password.length < 10) {
      setError('Das Passwort muss mindestens 10 Zeichen lang sein.');
      return;
    }

    if (!/\d/.test(formData.password)) {
      setError('Das Passwort muss mindestens eine Zahl (0-9) enthalten.');
      return;
    }

    if (formData.password !== formData.passwordConfirm) {
      setError('Passwörter stimmen nicht überein.');
      return;
    }

    if (!formData.acceptTerms) {
      setError('Bitte akzeptiere die AGB und Datenschutzerklärung.');
      return;
    }

    setLoading(true);
    setError('');
    const result = await register(formData);
    setLoading(false);

    if (result.requireVerification && onRequireVerification) {
      onRequireVerification(formData.email, result.devCode);
      return;
    }

    if (!result.success) {
      setError(result.error);
    }
  };

  const [socialModal, setSocialModal] = useState({
    open: false,
    provider: 'Google',
    email: '',
    name: '',
    error: '',
  });

  const parseJwt = (token) => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        window.atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (e) {
      return null;
    }
  };

  const handleSocialClick = async (provider) => {
    setError('');

    // Echter 1-Click Google OAuth Dialog
    if (provider === 'Google') {
      try {
        setLoading(true);
        const profile = await signInWithGooglePopup();
        const result = await socialLogin(
          'Google',
          profile.socialId,
          profile.email,
          profile.firstName || formData.firstName,
          profile.lastName || formData.lastName,
          profile.avatar,
          true // isOAuthVerified = true
        );
        setLoading(false);
        if (result.requireVerification && onRequireVerification) {
          onRequireVerification(result.email, result.devCode);
          return;
        }
        if (result.requireMFA && onRequireMFA) {
          onRequireMFA(result.email, result.devCode, result.mfaType);
          return;
        }
        if (!result.success) {
          setError(result.error);
        }
        return;
      } catch (err) {
        setLoading(false);
        console.warn('Google Popup Error / Cancelled:', err);
        if (err.message && !err.message.includes('popup_closed') && !err.message.includes('abgebrochen')) {
          setError(err.message);
          return;
        }
        return;
      }
    }

    // Google / Social Dialog direkt mit E-Mail-Eingabe öffnen
    setSocialModal({
      open: true,
      provider,
      email: formData.email.includes('@') ? formData.email : '',
      name: `${formData.firstName} ${formData.lastName}`.trim(),
      error: '',
    });
  };

  const handleSocialSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!socialModal.email || !socialModal.email.includes('@')) {
      setSocialModal(prev => ({ ...prev, error: 'Bitte eine gültige E-Mail-Adresse angeben.' }));
      return;
    }

    let firstName = socialModal.provider;
    let lastName = 'Nutzer';

    if (socialModal.name && socialModal.name.trim()) {
      const parts = socialModal.name.trim().split(' ');
      firstName = parts[0];
      lastName = parts.slice(1).join(' ') || 'Nutzer';
    } else {
      const usernamePart = socialModal.email.split('@')[0];
      const parts = usernamePart.split(/[._-]/);
      if (parts.length >= 2) {
        firstName = parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
        lastName = parts[1].charAt(0).toUpperCase() + parts[1].slice(1);
      } else if (parts.length === 1 && parts[0]) {
        firstName = parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
        lastName = 'Nutzer';
      }
    }

    setSocialModal(prev => ({ ...prev, submitting: true, error: '' }));

    const cleanEmail = socialModal.email.trim().toLowerCase();
    const socialId = `${socialModal.provider.toLowerCase()}-${cleanEmail}`;

    try {
      const result = await socialLogin(
        socialModal.provider,
        socialId,
        cleanEmail,
        firstName,
        lastName,
        '🌐',
        false
      );

      if (!result.success && !result.requireVerification && !result.requireMFA) {
        setSocialModal(prev => ({ ...prev, submitting: false, error: result.error || 'Registrierung fehlgeschlagen.' }));
      } else if (result.requireVerification && onRequireVerification) {
        setSocialModal(prev => ({ ...prev, open: false, submitting: false }));
        onRequireVerification(result.email, result.devCode);
      } else if (result.requireMFA && onRequireMFA) {
        setSocialModal(prev => ({ ...prev, open: false, submitting: false }));
        onRequireMFA(result.email, result.devCode, result.mfaType);
      } else {
        setSocialModal(prev => ({ ...prev, open: false, submitting: false }));
      }
    } catch (err) {
      setSocialModal(prev => ({ ...prev, submitting: false, error: 'Verbindungsfehler bei der Registrierung.' }));
    }
  };

  return (
    <>
      <Card sx={{ maxWidth: 560, mx: 'auto', mb: 4, borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.08)' }}>
        <CardContent sx={{ p: { xs: 3, md: 5 } }}>
          <Box textAlign="center" mb={3} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <NebenanHilfeLogo size="large" />
            <Typography variant="h5" component="h1" sx={{ fontWeight: 800, color: 'text.primary', mt: 2, mb: 0.5 }}>
              Konto erstellen
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
              Werde Teil der NebenanHilfe.de Gemeinschaft
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Social Media Login Options */}
          <Stack spacing={1.5} sx={{ mb: 3 }}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<GoogleIcon sx={{ color: '#ea4335' }} />}
              onClick={() => handleSocialClick('Google')}
              disabled={loading}
              sx={{ py: 1.2, textTransform: 'none', fontWeight: 600, borderColor: '#e0e0e0', color: 'text.primary', borderRadius: 2 }}
            >
              Mit Google registrieren
            </Button>
          </Stack>

          <Divider sx={{ my: 2.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ px: 1, fontWeight: 600 }}>
              ODER PER E-MAIL REGISTRIEREN
            </Typography>
          </Divider>

          <Box component="form" onSubmit={handleSubmit}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
              <TextField
                fullWidth
                label="Vorname"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                margin="normal"
                disabled={loading}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><PersonIcon color="action" /></InputAdornment>,
                }}
              />
              <TextField
                fullWidth
                label="Nachname"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                margin="normal"
                disabled={loading}
              />
            </Box>

            <TextField
              fullWidth
              label="E-Mail Adresse (für Bestätigungscode)"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              margin="normal"
              disabled={loading}
              InputProps={{
                startAdornment: <InputAdornment position="start"><EmailIcon color="action" /></InputAdornment>,
              }}
            />

            <TextField
              fullWidth
              label="Postleitzahl"
              name="postalCode"
              value={formData.postalCode}
              onChange={handleChange}
              margin="normal"
              inputProps={{ maxLength: 5 }}
              disabled={loading}
              InputProps={{
                startAdornment: <InputAdornment position="start"><LocationIcon color="action" /></InputAdornment>,
              }}
            />

            <TextField
              fullWidth
              label="Passwort"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleChange}
              margin="normal"
              disabled={loading}
              helperText={!formData.password ? "Mindestens 10 Zeichen mit mindestens einer Zahl (0-9)" : ""}
              InputProps={{
                startAdornment: <InputAdornment position="start"><PasswordIcon color="action" /></InputAdornment>,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="Sichtbarkeit umschalten"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Live Passwortstärke & Kriterien */}
            {formData.password && (
              <Box sx={{ mt: 1, mb: 1.5, p: 1.5, bgcolor: 'action.hover', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                {(() => {
                  const hasMinLen = formData.password.length >= 10;
                  const hasNumber = /\d/.test(formData.password);
                  const hasUpperLower = /[a-z]/.test(formData.password) && /[A-Z]/.test(formData.password);
                  const hasSpecial = /[^A-Za-z0-9]/.test(formData.password);
                  const score = (hasMinLen ? 1 : 0) + (hasNumber ? 1 : 0) + (hasUpperLower ? 1 : 0) + (hasSpecial ? 1 : 0);

                  const progressVal = Math.min(100, Math.round((score / 3.5) * 100));
                  const strengthLabel = score <= 1 ? 'Schwach' : score <= 2 ? 'Mittel' : 'Stark';
                  const strengthColor = score <= 1 ? 'error' : score <= 2 ? 'warning' : 'success';

                  return (
                    <>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.8 }}>
                        <Typography variant="caption" fontWeight={700} color="text.secondary">
                          Passwortstärke:
                        </Typography>
                        <Typography variant="caption" fontWeight={800} color={`${strengthColor}.main`}>
                          {strengthLabel}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={progressVal}
                        color={strengthColor}
                        sx={{ height: 6, borderRadius: 3, mb: 1.5 }}
                      />
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {hasMinLen ? (
                            <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
                          ) : (
                            <UncheckedIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                          )}
                          <Typography variant="caption" sx={{ color: hasMinLen ? 'success.dark' : 'text.secondary', fontWeight: hasMinLen ? 600 : 400 }}>
                            Mindestens 10 Zeichen (aktuell: {formData.password.length})
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {hasNumber ? (
                            <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
                          ) : (
                            <UncheckedIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                          )}
                          <Typography variant="caption" sx={{ color: hasNumber ? 'success.dark' : 'text.secondary', fontWeight: hasNumber ? 600 : 400 }}>
                            Mindestens eine Zahl (0-9)
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {hasUpperLower ? (
                            <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
                          ) : (
                            <UncheckedIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                          )}
                          <Typography variant="caption" sx={{ color: hasUpperLower ? 'success.dark' : 'text.secondary', fontWeight: hasUpperLower ? 600 : 400 }}>
                            Groß- und Kleinbuchstaben empfohlen
                          </Typography>
                        </Box>
                      </Box>
                    </>
                  );
                })()}
              </Box>
            )}

            <TextField
              fullWidth
              label="Passwort bestätigen"
              name="passwordConfirm"
              type={showPasswordConfirm ? 'text' : 'password'}
              value={formData.passwordConfirm}
              onChange={handleChange}
              margin="normal"
              disabled={loading}
              error={Boolean(formData.passwordConfirm && formData.password !== formData.passwordConfirm)}
              helperText={
                formData.passwordConfirm ? (
                  formData.password === formData.passwordConfirm ? (
                    <span style={{ color: '#2e7d32', fontWeight: 600 }}>✓ Passwörter stimmen überein</span>
                  ) : (
                    <span style={{ color: '#d32f2f', fontWeight: 600 }}>⚠ Eingaben stimmen nicht überein</span>
                  )
                ) : ''
              }
              InputProps={{
                startAdornment: <InputAdornment position="start"><PasswordIcon color="action" /></InputAdornment>,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="Sichtbarkeit umschalten"
                      onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
                      edge="end"
                    >
                      {showPasswordConfirm ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Legal Notice & AGB Acceptance Box with German Server Guarantee */}
            <Box
              sx={{
                mt: 3,
                p: 2.5,
                bgcolor: 'rgba(46, 125, 50, 0.04)',
                borderRadius: 2,
                border: '1px solid rgba(46, 125, 50, 0.2)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Chip
                  icon={<ServerIcon sx={{ fontSize: '1rem !important' }} />}
                  label="🇩🇪 Serverstandort Deutschland • 100% DSGVO"
                  size="small"
                  color="success"
                  variant="outlined"
                  sx={{ fontWeight: 700, fontSize: '0.78rem' }}
                />
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                Ihre Daten werden <strong>ausschließlich in Deutschland gespeichert</strong> und geschützt. Mit der Registrierung akzeptieren Sie unsere{' '}
                <Link
                  component="button"
                  type="button"
                  onClick={handleOpenAgb}
                  underline="always"
                  sx={{ fontWeight: 700, color: 'primary.main', cursor: 'pointer' }}
                >
                  AGB
                </Link>{' '}
                und{' '}
                <Link
                  component="button"
                  type="button"
                  onClick={handleOpenPrivacy}
                  underline="always"
                  sx={{ fontWeight: 700, color: 'primary.main', cursor: 'pointer' }}
                >
                  Datenschutzerklärung
                </Link>
                .
              </Typography>

              <FormGroup sx={{ mt: 1.5 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="acceptTerms"
                      checked={formData.acceptTerms}
                      onChange={handleChange}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2" fontWeight={600}>
                      Ich habe die AGB & Datenschutzerklärung gelesen und stimme zu.
                    </Typography>
                  }
                />
              </FormGroup>
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading || !formData.acceptTerms}
              startIcon={!loading ? <PersonAddIcon /> : null}
              sx={{
                mt: 3,
                mb: 3,
                py: 1.5,
                fontSize: '1.05rem',
                fontWeight: 700,
                borderRadius: 2,
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Konto erstellen & E-Mail bestätigen'}
            </Button>

            <Box textAlign="center">
              <Typography variant="body2">
                Bereits registriert?{' '}
                <Link
                  component="button"
                  type="button"
                  onClick={onSwitchToLogin}
                  sx={{ fontWeight: 700 }}
                >
                  Jetzt anmelden
                </Link>
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Interactive Legal Modal Dialog */}
      <Dialog
        open={legalModal.open}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, fontWeight: 800 }}>
          {legalModal.type === 'agb' ? <GavelIcon color="primary" /> : <SecurityIcon color="primary" />}
          {legalModal.title}
        </DialogTitle>
        <Divider />
        <DialogContent dividers>
          {legalModal.type === 'agb' ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="h6" fontWeight={700}>
                Allgemeine Geschäftsbedingungen (AGB) für NebenanHilfe.de
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Stand: August 2026
              </Typography>
              <Divider />
              <Typography variant="subtitle1" fontWeight={700}>
                1. Geltungsbereich & Vertragsgegenstand
              </Typography>
              <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                Diese Allgemeinen Geschäftsbedingungen gelten für die Nutzung der Nachbarschaftsplattform <strong>NebenanHilfe.de</strong>. Die Plattform ermöglicht nachbarschaftliche Hilfe, gegenseitige Unterstützung, lokales Leihen & Verschenken sowie Nachbarschaftskontakte im Quartier auf Grundlage christlicher Nächstenliebe und gegenseitigen Vertrauens.
              </Typography>

              <Typography variant="subtitle1" fontWeight={700}>
                2. 100% Kostenfreiheit & Gemeinnützigkeit
              </Typography>
              <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                - <strong>100% Kostenlos</strong>: Die Nutzung der gesamten Plattform NebenanHilfe.de ist für alle Bürgerinnen und Bürger vollständig kostenfrei.<br />
                - <strong>Keine Abos & Keine Gebühren</strong>: Es gibt keine Registrierungsgebühren, keine Mitgliedsbeiträge und keine Verträge.<br />
                - <strong>Nachbarschaftshilfe</strong>: Nachbarn unterstützen sich gegenseitig freiwillig, unentgeltlich und solidarisch.
              </Typography>

              <Typography variant="subtitle1" fontWeight={700}>
                3. Nutzerpflichten & Verhaltenskodex
              </Typography>
              <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                Alle Mitglieder verpflichten sich zu wahrheitsgemäßen Angaben, respektvollem Umgang und der Einhaltung geltender Gesetze. Beleidigungen, Diskriminierung, Spam oder kommerzieller Missbrauch sind strengstens untersagt.
              </Typography>

              <Typography variant="subtitle1" fontWeight={700} sx={{ color: 'success.dark', display: 'flex', alignItems: 'center', gap: 1 }}>
                🇩🇪 4. Serverstandort & Datenspeicherung in Deutschland
              </Typography>
              <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                Alle Daten unserer Nutzerinnen und Nutzer werden ausnahmslos auf zertifizierten Hochsicherheitsservern (ISO 27001) innerhalb der Bundesrepublik Deutschland (Rechenzentren in Nürnberg und Falkenstein) gespeichert und verarbeitet. Sämtliche Datenübertragungen sind per SSL/TLS verschlüsselt. Es erfolgt keinerlei unbefugte Weitergabe an Dritte oder Übermittlung in Drittländer.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="h6" fontWeight={700}>
                Datenschutzerklärung (DSGVO) für NebenanHilfe.de
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Stand: August 2026
              </Typography>
              <Divider />
              <Typography variant="subtitle1" fontWeight={700}>
                1. Verantwortlicher für die Datenverarbeitung
              </Typography>
              <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                Verantwortlich im Sinne der DSGVO ist das Betreiberteam von <strong>NebenanHilfe.de</strong> (support@nebenanhilfe.de).
              </Typography>

              <Typography variant="subtitle1" fontWeight={700}>
                2. Erhobene Daten & Verwendungszweck
              </Typography>
              <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                Wir verarbeiten personenbezogene Daten (Vorname, Nachname, E-Mail-Adresse, Telefonnummer, Postleitzahl) ausschließlich zur Bereitstellung der lokalen Nachbarschaftshilfe, Verifizierung von Quartierangeboten und sicheren Kommunikation im System.
              </Typography>

              <Typography variant="subtitle1" fontWeight={700} sx={{ color: 'success.dark', display: 'flex', alignItems: 'center', gap: 1 }}>
                🇩🇪 3. Serverstandort Deutschland (100% DSGVO & BDSG)
              </Typography>
              <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                Ihre Daten sind sicher: Alle Datenbanken, Benutzerprofile, Chatnachrichten und Medien werden ausschließlich auf Servern in Deutschland gehostet (ISO 27001 zertifizierte Rechenzentren). Dies garantiert höchste Sicherheitsstandards nach deutschem Bundesdatenschutzgesetz (BDSG) und der EU-Datenschutz-Grundverordnung (DSGVO).
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseModal} variant="contained" sx={{ px: 3, fontWeight: 700 }}>
            Schließen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Social Media Login/Registration Modal */}
      <Dialog 
        open={socialModal.open} 
        onClose={() => setSocialModal(prev => ({ ...prev, open: false }))}
        PaperProps={{ sx: { borderRadius: 3, p: 1, maxWidth: 450, width: '100%' } }}
      >
        <DialogTitle sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
          {socialModal.provider === 'Google' && <GoogleIcon sx={{ color: '#ea4335', fontSize: 28 }} />}
          {socialModal.provider === 'Apple' && <AppleIcon sx={{ fontSize: 28 }} />}
          {socialModal.provider === 'Facebook' && <FacebookIcon sx={{ color: '#1877f2', fontSize: 28 }} />}
          Mit {socialModal.provider} registrieren
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
            Gib deine {socialModal.provider}-Adresse ein, um dich direkt anzumelden oder ein kostenloses Konto zu erstellen:
          </Typography>

          {socialModal.error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {socialModal.error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSocialSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              autoFocus
              label={`${socialModal.provider}-Adresse`}
              type="email"
              placeholder={socialModal.provider === 'Google' ? 'dein.name@googlemail.com' : 'deine.email@beispiel.de'}
              value={socialModal.email}
              onChange={(e) => setSocialModal(prev => ({ ...prev, email: e.target.value, error: '' }))}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Dein vollständiger Name"
              placeholder="z. B. Max Mustermann"
              value={socialModal.name}
              onChange={(e) => setSocialModal(prev => ({ ...prev, name: e.target.value }))}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={socialModal.submitting}
              sx={{
                mt: 1,
                py: 1.3,
                fontWeight: 700,
                backgroundColor: socialModal.provider === 'Google' ? '#1a73e8' : undefined,
                '&:hover': {
                  backgroundColor: socialModal.provider === 'Google' ? '#1557b0' : undefined,
                }
              }}
            >
              {socialModal.submitting ? <CircularProgress size={24} color="inherit" /> : `Weiter mit ${socialModal.provider}`}
            </Button>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => setSocialModal(prev => ({ ...prev, open: false }))} 
            color="inherit"
            disabled={socialModal.submitting}
          >
            Abbrechen
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RegisterForm;
